/**
 * Build the POST body for /cascade-tracing/orders/purchases/list from
 * useTable's queryParams + isArchived flag.
 */
export function buildPurchaseOrdersApiBody(params: {
  page: number;
  limit: number;
  isArchived: boolean;
  sort?: string;
  filters?: Record<string, string[]>;
}): Record<string, unknown> {
  const body: Record<string, unknown> = {
    page: params.page,
    limit: params.limit,
    isArchived: params.isArchived,
  };

  if (params.sort) {
    const [field, dir] = params.sort.split(':');
    if (field) body.sort = field;
    if (dir) body.order = dir;
  }

  const f = params.filters ?? {};

  if (f.orderNumber?.[0]) body.orderNumber = f.orderNumber[0];
  if (f.tracingSalesOrderNumber?.[0])
    body.tracingSalesOrderNumber = f.tracingSalesOrderNumber[0];
  if (f.supplierCompanyId?.length)
    body.supplierCompanyId = f.supplierCompanyId.join(',');
  if (f.status?.length) body.status = f.status.join(',');
  if (f.lastProductionProcessId?.[0])
    body.lastProductionProcessId = f.lastProductionProcessId[0];
  if (f.orderType?.length) body.orderType = f.orderType.join(',');
  if (f.receiverCompanyId?.[0]) body.receiverCompanyId = f.receiverCompanyId[0];
  if (f.tags?.length) body.tags = f.tags.join(',');
  if (f.productId?.length) body.productId = f.productId.join(',');

  return body;
}

/**
 * Build the POST body for /cascade-tracing/orders/internal/list from
 * useTable's queryParams + isArchived flag.
 *
 * Mirrors buildPurchaseOrdersApiBody but uses buyerCompanyId instead of
 * supplierCompanyId as the primary company filter (matching old frontend).
 */
export function buildInternalOrdersApiBody(params: {
  page: number;
  limit: number;
  isArchived: boolean;
  sort?: string;
  filters?: Record<string, string[]>;
}): Record<string, unknown> {
  const body: Record<string, unknown> = {
    page: params.page,
    limit: params.limit,
    isArchived: params.isArchived,
  };

  if (params.sort) {
    const [field, dir] = params.sort.split(':');
    if (field) body.sort = field;
    if (dir) body.order = dir;
  }

  const f = params.filters ?? {};

  if (f.orderNumber?.[0]) body.orderNumber = f.orderNumber[0];
  if (f.supplierCompanyId?.length)
    body.supplierCompanyId = f.supplierCompanyId.join(',');
  if (f.buyerCompanyId?.length)
    body.buyerCompanyId = f.buyerCompanyId.join(',');
  if (f.status?.length) body.status = f.status.join(',');
  if (f.lastProductionProcessId?.[0])
    body.lastProductionProcessId = f.lastProductionProcessId[0];
  if (f.orderType?.length) body.orderType = f.orderType.join(',');
  if (f.tags?.length) body.tags = f.tags.join(',');
  if (f.productId?.length) body.productId = f.productId.join(',');

  return body;
}

/**
 * Build the POST body for /cascade-tracing/orders/sales/list from
 * useTable's queryParams + isArchived flag.
 *
 * The API expects individual filter fields (orderNumber, buyerCompanyId…)
 * rather than a single serialised filter string.
 */
export function buildSalesOrdersApiBody(params: {
  page: number;
  limit: number;
  isArchived: boolean;
  sort?: string; // 'fieldName:ASC' or 'fieldName:DESC' from useTable
  filters?: Record<string, string[]>;
}): Record<string, unknown> {
  const body: Record<string, unknown> = {
    page: params.page,
    limit: params.limit,
    isArchived: params.isArchived,
  };

  if (params.sort) {
    const [field, dir] = params.sort.split(':');
    if (field) body.sort = field;
    if (dir) body.order = dir; // 'ASC' | 'DESC'
  }

  const f = params.filters ?? {};

  if (f.orderNumber?.[0]) body.orderNumber = f.orderNumber[0];
  if (f.tracingPurchaseOrderNumber?.[0])
    body.tracingPurchaseOrderNumber = f.tracingPurchaseOrderNumber[0];
  if (f.buyerCompanyId?.length)
    body.buyerCompanyId = f.buyerCompanyId.join(',');
  if (f.status?.length) body.status = f.status.join(',');
  if (f.lastProductionProcessId?.[0])
    body.lastProductionProcessId = f.lastProductionProcessId[0];
  if (f.orderType?.length) body.orderType = f.orderType.join(',');
  if (f.receiverCompanyId?.[0]) body.receiverCompanyId = f.receiverCompanyId[0];
  if (f.tags?.length) body.tags = f.tags.join(',');
  if (f.productId?.length) body.productId = f.productId.join(',');

  return body;
}
